import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Offcanvas, Table, Form, Button, Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserFriends,
  faTrash,
  faPlus,
  faListAlt,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import IconAlert from '../../../../../common/IconAlert';
import { toast } from 'react-toastify';
import api from '@services/api';

const ParticipantsOffcanvas = ({
  show,
  onHide,
  currentEntity,
  participants,
  newParticipant,
  onNewParticipantChange,
  onAddParticipant,
  onRemoveParticipant,
  entityType,
  onOpenWishes,
  salonId,
  onParticipantsUpdate
}) => {
  // Déterminer le type d'entité (partenaire ou entreprise)
  const isPartner = entityType === 'partner';
  const entityIdField = isPartner ? 'partner_id' : 'company_id';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null); // Track which participant is being deleted

  // Filtrer les participants pour n'afficher que ceux liés à l'entité actuelle
  const filteredParticipants = participants.filter(
    p => p[entityIdField] === currentEntity?.id
  );

  const handleAddParticipant = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        [entityIdField]: currentEntity ? currentEntity.id : null,
        firstname: newParticipant.firstname,
        lastname: newParticipant.lastname,
        email: newParticipant.email,
        phone: newParticipant.phone
      };

      // CORRECTION DE L'URL
      const response = await api.post(
        `/admin/partner-salons/${salonId}/participants/${
          isPartner ? 'partner' : 'company'
        }`,
        payload
      );

      if (response.data.success) {
        // Mettre à jour l'UI
        onAddParticipant(response.data.data.participant);

        // Mettre à jour le compteur de participants et de voeux
        if (isPartner) {
          onParticipantsUpdate(
            {
              id: response.data.data.partner.id,
              participants_count: response.data.data.partner_participants_count,
              wishes_count: response.data.data.wishes_count
            },
            entityType
          );
        } else {
          onParticipantsUpdate(
            {
              id: response.data.data.company.id,
              participants_count: response.data.data.company_participants_count,
              wishes_count: response.data.data.wishes_count
            },
            entityType
          );
        }

        toast('Participant ajouté avec succès');
      } else {
        toast(response.data.message || "Erreur lors de l'ajout du participant");
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout du participant:", error);
      toast(
        error.response?.data?.message || "Erreur lors de l'ajout du participant"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveParticipant = async participantId => {
    setIsDeleting(participantId); // Set the deleting state for this participant
    try {
      // CORRECTION DE L'URL
      const endpoint = `/admin/partner-salons/${salonId}/participants/${
        isPartner ? 'partner' : 'company'
      }/${participantId}`;

      const response = await api.delete(endpoint);

      if (response.data.success) {
        // Mettre à jour l'UI
        onRemoveParticipant(participantId);

        // Mettre à jour le compteur de participants et de voeux
        if (isPartner) {
          onParticipantsUpdate(
            {
              id: response.data.data.partner.id,
              participants_count: response.data.data.partner_participants_count,
              wishes_count: response.data.data.wishes_count
            },
            entityType
          );
        } else {
          onParticipantsUpdate(
            {
              id: response.data.data.company.id,
              participants_count: response.data.data.company_participants_count,
              wishes_count: response.data.data.wishes_count
            },
            entityType
          );
        }
        toast('Participant supprimé avec succès');
      } else {
        toast(
          response.data.message ||
            'Erreur lors de la suppression du participant'
        );
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du participant:', error);
      toast(
        error.response?.data?.message ||
          'Erreur lors de la suppression du participant'
      );
    } finally {
      setIsDeleting(null); // Clear the deleting state
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className={`offcanvas-${isPartner ? 'partner' : 'company'}Participants`}
      style={{
        width: '700px',
        zIndex: 1060
      }}
    >
      <Offcanvas.Header closeButton className="bg-light">
        <Offcanvas.Title>
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faUserFriends} className="me-2" />
            Participants de {currentEntity?.name || ''}
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <IconAlert variant="info" className="mb-4">
          <p className="mb-0">
            Ajoutez les participants qui représenteront {currentEntity?.name}{' '}
            lors de ce salon.
          </p>
        </IconAlert>

        <h5 className="mb-3">Participants actuels</h5>
        {filteredParticipants.length > 0 ? (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Prénom</th>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th width="120">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredParticipants.map(participant => (
                <tr key={participant.id}>
                  <td>
                    <span style={{ marginLeft: '12px', display: 'block' }}>
                      {participant.firstname}
                    </span>
                  </td>
                  <td>{participant.lastname}</td>
                  <td>{participant.email}</td>
                  <td>{participant.phone}</td>
                  <td>
                    <div className="d-flex justify-content-center">
                      {onOpenWishes && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => onOpenWishes(participant, entityType)}
                          title="Gérer les vœux"
                          disabled={
                            isSubmitting || isDeleting === participant.id
                          }
                        >
                          <FontAwesomeIcon icon={faListAlt} />
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveParticipant(participant.id)}
                        title="Supprimer"
                        disabled={isSubmitting || isDeleting === participant.id}
                      >
                        {isDeleting === participant.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faTrash} />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="text-center py-4 border rounded bg-light">
            <p className="text-muted mb-0">
              Aucun participant pour{' '}
              {isPartner ? 'ce partenaire' : 'cette société'}.
            </p>
          </div>
        )}

        <Card className="mt-4 shadow-none border">
          <Card.Header className="bg-light">
            <h5 className="mb-0 fs-0">Ajouter un participant</h5>
          </Card.Header>
          <Card.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Prénom</Form.Label>
                <Form.Control
                  type="text"
                  name="firstname"
                  value={newParticipant.firstname}
                  onChange={onNewParticipantChange}
                  placeholder="Prénom du participant"
                  disabled={isSubmitting}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Nom</Form.Label>
                <Form.Control
                  type="text"
                  name="lastname"
                  value={newParticipant.lastname}
                  onChange={onNewParticipantChange}
                  placeholder="Nom du participant"
                  disabled={isSubmitting}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="text"
                  name="email"
                  value={newParticipant.email}
                  onChange={onNewParticipantChange}
                  placeholder="Email du participant"
                  disabled={isSubmitting}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Téléphone</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={newParticipant.phone}
                  onChange={onNewParticipantChange}
                  placeholder="Téléphone du participant"
                  disabled={isSubmitting}
                />
              </Form.Group>
              <Button
                variant="falcon-primary"
                className="w-100"
                onClick={handleAddParticipant}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Ajout en cours...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Ajouter le participant
                  </>
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        <div className="d-grid mt-4">
          <Button
            variant="falcon-default"
            size="lg"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Fermer
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

ParticipantsOffcanvas.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  currentEntity: PropTypes.object,
  participants: PropTypes.array,
  newParticipant: PropTypes.object.isRequired,
  onNewParticipantChange: PropTypes.func.isRequired,
  onAddParticipant: PropTypes.func.isRequired,
  onRemoveParticipant: PropTypes.func.isRequired,
  entityType: PropTypes.oneOf(['partner', 'company']).isRequired,
  onOpenWishes: PropTypes.func,
  salonId: PropTypes.string.isRequired,
  onParticipantsUpdate: PropTypes.func.isRequired // Nouvelle prop
};

ParticipantsOffcanvas.defaultProps = {
  participants: [],
  currentEntity: null
};

export default ParticipantsOffcanvas;
